import { z } from 'zod';
import { getNormalizedDataSourceFields } from '@/services/data-sources/data-sources-service';
import { generateFilterSystemPrompt } from './dashboard-filters-system-prompt';
import { translateFiltersFromJsonFunctionCall } from './translate-filters';
import { DashboardProps } from '@sisense/sdk-ui';
import { getDashboardDataSourcesMap } from '@/services/dashboards/dashboards-service';
import { mergeFiltersOrFilterRelations } from '@sisense/sdk-data';
import { createStructuredCompletion } from '@/utils/chat-completion-utils';
import { OpenAI } from 'openai';

const schema = z.object({
	filtersJson: z.string().describe('A JSON string representing the filters'),
	explanation: z
		.string()
		.describe(
			'A non-technical explanation of how the filters were manipulated',
		),
});

export const manipulateDashboardFilters = async (
	userPrompt: string,
	dataSourceTitle: string,
	dashboardProps: DashboardProps,
	openAIClient?: OpenAI,
): Promise<{ dashboardProps: DashboardProps; explanation: string }> => {
	const dataSourcesMap = getDashboardDataSourcesMap(dashboardProps);
	const dataSource = dataSourcesMap.get(dataSourceTitle);
	if (!dataSource) {
		throw new Error(
			`Data source "${dataSourceTitle}" not found in dashboard. Please ask the user to specify a valid data source.`,
		);
	}

	const systemPrompt = generateFilterSystemPrompt();

	const dataFields = await getNormalizedDataSourceFields(dataSourceTitle);

	const fullUserPrompt = `${userPrompt}\n\nAvailable Data Fields:\n${JSON.stringify(
		dataFields,
		null,
		2,
	)}`;

	const response = await createStructuredCompletion({
		systemPrompt,
		userPrompt: fullUserPrompt,
		openAIClient,
		responseFormat: {
			type: 'json_schema',
			schema,
			name: 'filters',
		},
	});

	const { filtersJson, explanation } = response;
	if (!filtersJson) {
		throw new Error('Failed to manipulate filters');
	}

	const filters = translateFiltersFromJsonFunctionCall(
		filtersJson,
		dataSource,
		dataFields,
	);

	// Always return local dashboard props with merged filters
	const updatedDashboardProps = {
		...dashboardProps,
		filters: mergeFiltersOrFilterRelations(dashboardProps.filters, filters),
	};

	return {
		dashboardProps: updatedDashboardProps,
		explanation,
	};
};
